/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import { Button, INodeProps } from '@msdyn365-commerce-modules/utilities';
import { getUrlSync } from '@msdyn365-commerce/core';
import {
    ChannelIdentity,
    Customer,
    OrderOriginator,
    OrderShipments,
    OrgUnitLocation,
    SimpleProduct
} from '@msdyn365-commerce/retail-proxy';
import * as React from 'react';
import { getGroups, IGroups } from '../common/get-groups';
import { getOrderInfomationInput, getOrderInformation, IOrderInformation } from '../common/get-order-information';
import { getSalesLines, getSalesLinesInput } from '../common/get-sales-lines';
import { IMandsOrderHistoryData } from '../mands-order-history.data';
import { IMandsOrderHistoryProps } from '../mands-order-history.props.autogenerated';

export interface ISalesOrderProps extends IMandsOrderHistoryProps<IMandsOrderHistoryData> {
    salesOrder: OrderShipments;
    products: SimpleProduct[];
    channelIdentities?: ChannelIdentity[];
    orgUnitLocations?: OrgUnitLocation[];
    showChannelInfo?: boolean;
    customer?: Customer;
    originator?: OrderOriginator;
}

export interface ISalesOrder {
    salesOrderProps: INodeProps;
    orderInfomation: IOrderInformation;
    groups?: IGroups;
    orderDetailsLink: React.ReactNode;
}

export const getSalesOrder = (input: ISalesOrderProps): ISalesOrder => {
    const {
        id,
        typeName,
        context,
        telemetry,
        products,
        salesOrder,
        showChannelInfo,
        channelIdentities,
        orgUnitLocations,
        customer,
        originator,
        resources: { freePriceText, detailsAriaLabel, detailsLabel, orderItemLabel, orderItemsLabel },
        context: { actionContext }
    } = input;

    const moduleName = 'ms-order-history';

    const priceContext = {
        id,
        typeName,
        context,
        telemetry,
        freePriceText
    };

    const _getorderDetailsPageUrl = (): string => {
        const url = getUrlSync('orderDetails', actionContext) || '';
        const separator = url.includes('?') ? '&' : '?';
        if (salesOrder.SalesId) {
            // Redirect with sales order sales id
            return `${url}${separator}salesId=${salesOrder.SalesId}`;
        } else {
            // Redirect with sales order transaction id
            return `${url}${separator}transactionId=${salesOrder.TransactionId}`;
        }
    };

    const salesOrderProps = { className: 'ms-order-history__sales-order', key: salesOrder.SalesId || salesOrder.TransactionId };

    const orderInfomationInput = getOrderInfomationInput(
        input,
        moduleName,
        salesOrder,
        priceContext,
        channelIdentities,
        customer,
        originator,
        orgUnitLocations,
        showChannelInfo
    );
    const orderInfomation = getOrderInformation(orderInfomationInput);

    const salesLinesInput = products && getSalesLinesInput(input, moduleName, salesOrder, products, priceContext);
    const salesLines = salesLinesInput && getSalesLines({ ...salesLinesInput, imageOnly: true });

    const groups =
        salesLines &&
        getGroups({
            moduleName,
            salesLines,
            resources: { orderItemLabel, orderItemsLabel }
        });

    const orderDetailsLink = (
        <Button
            href={_getorderDetailsPageUrl()}
            aria-label={
                salesOrder.ChannelReferenceId && detailsAriaLabel
                    ? detailsAriaLabel.replace('{orderId}', salesOrder.ChannelReferenceId)
                    : ''
            }
            className='ms-order-history__btn-order-details'
        >
            {detailsLabel}
        </Button>
    );

    return {
        salesOrderProps,
        orderInfomation,
        groups,
        orderDetailsLink
    };
};
